<template>
  <q-page>
    <div class="justify-center row">
      <div v-if="!$apollo.queries.containerOrder.loading" class="col-10 q-pa-md">
        <h2 class="q-mt-sm q-mb-sm text-center">Order {{containerOrder.orderNumber}}</h2>
        <div class="text-h4 text-center">({{containerOrder.name}})</div>
        <h3>Details</h3>
        <div class="col q-gutter-md">
          <div class="row items-center">
            <div style="width: 8em; text-align: right;">Company:</div>
            <div class="col-auto q-pl-md">
              <router-link :to="`/admin/accounts/${containerOrder.account.id}`">{{containerOrder.account.companyName}}</router-link>
            </div>
          </div>
          <div class="row items-center">
            <div style="width: 8em; text-align: right;">Group Order:</div>
            <div class="col-auto q-pl-md">
              <router-link :to="`/admin/group-orders/${containerOrder.groupOrder.id}`">{{containerOrder.groupOrder.name}} ({{containerOrder.groupOrder.isOpen ? 'Open' : 'Closed'}})</router-link>
            </div>
          </div>

          <m-edit-item field="placed" type="datetime" graphqlType="ContainerOrder" :id="id" updateFunction="" />
          <div class="row">
            <m-edit-item field="verified" type="datetime" graphqlType="ContainerOrder" :id="id" :updateFunction="updateVerified" />
            <xero-link type="ContainerOrder" :id="id" class="q-ml-md" v-if="containerOrder.verified"/>
          </div>
          <m-edit-item field="processed" type="datetime" graphqlType="ContainerOrder" :id="id" />
          <m-edit-item field="leftManufacturer" type="datetime" graphqlType="ContainerOrder" :id="id" />
          <m-edit-item field="shippedFromICP" type="datetime" graphqlType="ContainerOrder" :id="id" />
          <m-edit-item field="delivered" type="datetime" graphqlType="ContainerOrder" :id="id" />
        </div>
        <h3 class="q-mb-none">Notes</h3>
        <m-edit-item label-type="none" autogrow field="notes" type="input" graphqlType="ContainerOrder" :id="id"/>
        <h3>Container Order</h3>
        <q-table
          :data="containerOrder.items"
          :columns="orderColumns"
          :pagination="{rowsPerPage: 0}"
          binary-state-sort
          row-key="name" >
          <template v-slot:body-cell-img="props">
            <q-td>
              <q-img dense :src="props.value + '.png.thumb'" style="width: 25px; height: 25px;" contain>
                <q-tooltip anchor="center right" self="center left" :offset="[10, 10]"
                           transition-show="scale"
                           transition-hide="scale"
                           content-style="background: white; border: solid 1px black">
                  <q-img dense :src="props.value" style="width: 25vw; height: 25vw;" contain/>
                </q-tooltip>
              </q-img>
            </q-td>
          </template>

          <template v-slot:body-cell-reference="props">
            <q-td :props="props">
              <router-link :to="`/admin/products/${((props.row.sku||{}).product||{}).id}`">{{props.value}}</router-link>
            </q-td>
          </template>
          <template v-slot:body-cell-discountPercent="props">
            <q-td :props="props">
              {{props.value}}
              <m-popup-edit select-on-focus
                            :value="Number(props.row.discountPercent).toFixed(2)" inputType="number" v-if="!containerOrder.verified"
                            @save="v => editOrderItem(props.row.skuId, { discountPercent: Number(v) })" :buttons="false"/>
            </q-td>
          </template>

          <template v-slot:body-cell-colour="props">
            <q-td :props="props">
              <div :style="{border: '1px silver solid', backgroundColor: props.row.sku.options.colour.hex, width: '10px',height: '10px',marginRight:'5px'}" class="inline-block"></div>
              {{props.row.sku.options.colour.group + ' (' + props.row.sku.options.colour.name + ')'}}
            </q-td>
          </template>

          <template v-slot:body-cell-total="props">
            <q-td :props="props">
              {{props.value}}
              <m-popup-edit select-on-focus
                            :value="Number(props.row.totalCost).toFixed(2)" inputType="number" v-if="!containerOrder.verified"
                            @save="v => editOrderItem(props.row.skuId, { totalCost: Number(v) })" :buttons="false"/>
            </q-td>
          </template>

          <template v-slot:body-cell-price="props">
            <q-td :props="props">
              <q-btn icon="update" dense flat size="xs" v-if="!containerOrder.verified && !itemCostIsSameAsSkuPrice(props)"
                     @click="updateItemPriceToSkuPrice(props)">
                <q-tooltip>Update to current sku price</q-tooltip>
              </q-btn>
              <span>
                {{props.value}}
                <m-popup-edit select-on-focus
                              :value="Number(props.row.itemCost).toFixed(2)" inputType="number" v-if="!containerOrder.verified"
                              @save="v => editOrderItem(props.row.skuId, { itemCost: Number(v) })" :buttons="false"/>
              </span>
            </q-td>
          </template>

          <template v-slot:body-cell-qty="props">
            <q-td :props="props" @click="!order.verified && editItemQty(props.row)">
              {{props.value}}
            </q-td>
          </template>

          <template v-slot:body-cell-actions="props">
            <q-td key="actions" :props="props">
              <span>
                <q-btn dense flat color="red" icon="clear" size="sm" @click="deleteItem(props.row)" :disabled="containerOrder.verified"></q-btn>
                <q-tooltip v-if="order.verified">Can't edit order once it's been verified.</q-tooltip>
              </span>
            </q-td>
          </template>

          <template v-slot:bottom-row v-if="!containerOrder.verified">
            <q-tr>
              <q-td colspan="100%">
                <q-select
                  v-if="addProductsMode"
                  filled
                  v-model="productToAdd"
                  use-input
                  dense
                  hide-selected
                  @input="addItem"
                  input-debounce="0"
                  label="Add A Product"
                  :options="products"
                  @filter="filterFn"
                >
                  <template v-slot:no-option>
                    <q-item>
                      <q-item-section class="text-grey">
                        No results
                      </q-item-section>
                    </q-item>
                  </template>
                  <template v-slot:option="scope">
                    <q-item
                      v-bind="scope.itemProps"
                      v-on="scope.itemEvents" >
                      <q-item-section side>
                        <q-img :src="scope.opt.imageUrl" style="width: 40px; height: 40px;" contain/>
                      </q-item-section>
                      <q-item-section>
                        <q-item-label>{{scope.opt.label}}</q-item-label>
                      </q-item-section>
                    </q-item>
                  </template>
                </q-select>
                <q-btn class="full-width" flat v-if="!addProductsMode" @click="addProductsMode = true">Add Products</q-btn>
              </q-td>
            </q-tr>
          </template>
        </q-table>

        <div class="row full-width justify-end q-pt-md">
          <q-btn class="col-auto" flat color="primary" @click="discountWholeOrder">Apply A Discount To Whole Order</q-btn>
        </div>
        <div class="text-h4 q-pt-md text-right">
          Total Cost: {{ formatCurrency(containerOrder.totalCost) }}
        </div>
        <div class="text-h5 text-right text-grey">
          Includes GST: {{ formatCurrency(containerOrder.totalCost*(1-1/1.1)) }}
        </div>

        <h3 class="q-mt-none q-mb-md">Break Downs</h3>
        <div class="row">
          <div class="col-12 col-sm-12 col-md-6 col-lg-4 col-xl-3 q-pa-sm">
            <summary-table
              title="Brand"
              :items="containerOrder.items"
            />
          </div>
          <div class="col-12 col-sm-12 col-md-6 col-lg-4 col-xl-3 q-pa-sm">
            <summary-table
              title="Type"
              :items="containerOrder.items"
            />
          </div>
          <div class="col-12 col-sm-12 col-md-6 col-lg-4 col-xl-3 q-pa-sm">
            <summary-table
              title="Material"
              :items="containerOrder.items"
            />
          </div>
          <div class="col-12 col-sm-12 col-md-6 col-lg-4 col-xl-3 q-pa-sm">
            <summary-table
              title="Colour"
              :items="containerOrder.items"
              :group-field-callback="row => (((row.sku||{}).options||{}).colour||{}).group">
              <template v-slot:group="props">
                <div :style="{border: '1px solid silver',backgroundColor: (((props.row.item.sku||{}).options||{}).colour||{}).hex, width: '10px',height: '10px'}" class="inline-block">
                </div>
                {{props.row.group}}
              </template>
            </summary-table>
          </div>
          <div class="col-12 col-sm-12 col-md-6 col-lg-4 col-xl-3 q-pa-sm">
          <summary-table
            title="Screws You'll Need"
            sumName="Qty"
            groupName="Length"
            :items="containerOrder.items"
            :group-field-callback="row => getProductAttributeFromRow('screws', row).map(x => x.type)"
            :sum-callback="row => getProductAttributeFromRow('screws', row).map(x => x.qty * row.qty)"
            :hide-price-column="true">
          </summary-table>
          </div>          
          <div class="col-12 col-sm-12 col-md-6 col-lg-4 col-xl-3 q-pa-sm">
          <summary-table
            title="Bolts You'll Need"
            sumName="Qty"
            groupName="Length"
            :items="containerOrder.items"
            :group-field-callback="row => getProductAttributeFromRow('bolts', row).map(x => x.type)"
            :sum-callback="row => getProductAttributeFromRow('bolts', row).map(x => x.qty * row.qty)"
            :hide-price-column="true">
          </summary-table>
          </div>
        </div>
      </div>
    </div>
  </q-page>
</template>

<style>
</style>

<script>
import gql from 'graphql-tag'
import MEditItem from '../../components/ui/admin/MEditItem'
import { colourToString, formatCurrency } from '../../util/string'
import adminNotifyError from '../../util/adminNotifyError'
import MPopupEdit from '../../components/ui/MPopupEdit'
import SummaryTable from '../../components/summaryTable'
import XeroLink from '../../components/XeroLink'
import { makeSearchFilter } from '../../store/filters/getters'
import getNestedFieldInObject from '@/util/getNestedFieldInObject'

export default {
  name: 'PageAdminContainerOrder',
  components: { XeroLink, MPopupEdit, MEditItem, SummaryTable },
  apollo: {
    containerOrder () {
      return {
        query: gql`
        query containerOrder ($id: ID!) {
          containerOrder (id: $id) {
            id
            orderNumber
            groupOrder {
              id
              name
              isOpen
            }
            name
            notes
            createdAt
            totalCost
            account {
              id
              companyName
            }

            placed
            verified
            processed
            leftManufacturer
            shippedFromICP
            delivered

            items {
              id
              skuId
              sku {
                  id
                  options
                  price
                  containerPrice
                  stock
                  product {
                      id
                      name
                      reference
                      attributes
                      images {
                        url
                      }
                      urlSlug
                  }
              }
              qty
              notes
              itemCost
              discountPercent
              totalCost
            }
          }
        }
      `,
        variables: {
          id: this.id
        },
        fetchPolicy: 'network-only'
      }
    },
    products () {
      return {
        query: gql`
          query products ($filter: ProductFilterInput, $limit: Int, $offset: Int, $orderBy: ProductsOrderByFieldInput, $descending: SortOrderInput) {
            products(filter: $filter, store: AllProducts, limit: $limit, offset: $offset, orderBy: $orderBy, descending: $descending) {
              products {
                id
                name
                reference
                attributes
                images {
                  url
                },
                skus {
                  id
                  showInContainer
                  containerPrice
                  options
                }
              }
            }
          }
        `,
        variables () {
          return {
            filter: makeSearchFilter(this.searchTerm),
            limit: 20,
            offset: 0,
            orderBy: 'name',
            descending: 'Ascending'
          }
        },
        update (result) {
          const retVal = result.products?.products.flatMap(product => {
            return product.skus.map(sku => {
              return Object.assign({
                skuId: sku.id,
                colour: sku.options.colour,
                imageUrl: product.images?.[0]?.url,
                // label: `${product.reference} - ${product.attributes.brand} - ${product.name} - ${sku.options.colour.group} (${sku.options.colour.name})`
                label: product.reference + " - " + product.attributes.brand + " - " + product.name + " - " +sku.options.colour.group + " (" + sku.options.colour.name + ")"
              }, product)
            }).filter(x => x !== null)
          })
          if (this.prodUpdateFunc) {
            this.prodUpdateFunc()
            this.prodUpdateFunc = null
          }
          return retVal
        },
        skip: () => !this.addProductsMode
      }
    }
  },
  computed: {
    id () {
      return this.$route.params.id
    },
    dataBrandSummary () {
      return this.summariseByGroup(row => row.sku?.product?.attributes?.brand, row => row.sku?.product?.attributes?.holds * row.qty)
    },
    dataSizeSummary () {
      return this.summariseByGroup(row => row.sku?.product?.attributes?.size, row => row.sku?.product?.attributes?.holds * row.qty)
    },
    dataGripSummary () {
      return this.summariseByGroup(row => row.sku?.product?.attributes?.grip, row => row.sku?.product?.attributes?.holds * row.qty)
    },
    dataColourSummary () {
      return this.summariseByGroup(
        row => this.colourToString(row.sku?.options?.colour),
        row => row.sku?.product?.attributes?.holds * row.qty
      )
    }
  },
  methods: {
    getNestedFieldInObject: getNestedFieldInObject,
    getProductAttributeFromRow (attributeName, row) {
      return this.getNestedFieldInObject(`sku.product.attributes.${attributeName}`, row) || []
    },
    formatCurrency: formatCurrency,
    colourToString (colour) {
      try {
        return colour.group + ' (' + colour.name + ')'
      } catch (ex) {
        return ''
      }
    },
    summariseByGroup (groupFieldCallback, sumCallback, groupName, sumName) {
      let map = (this.containerOrder.items || []).reduce((result, row) => {
        if (!(groupFieldCallback(row) in result)) {
          result[groupFieldCallback(row)] = 0
        }
        result[groupFieldCallback(row)] += sumCallback(row)
        return result
      }, {})
      return Object.keys(map).map(key => { return { group: key, value: map[key] } }).filter(o => o)
    },
    asyncDialog (config) {
      return new Promise((resolve, reject) => {
        this.$q.dialog(config).onOk(resolve).onCancel(reject)
      })
    },
    updateItemPriceToSkuPrice (props) {
      if (props.row?.sku?.containerPrice != null) {
        this.editOrderItem(props.row.skuId, { itemCost: Number(props.row?.sku?.containerPrice) })
      }
    },
    itemCostIsSameAsSkuPrice (props) {
      return props.row?.sku?.containerPrice === props.row.itemCost
    },
    async editOrderItem (skuId, changes) {
      try {
        await this.$apollo.mutate({
          mutation: gql`mutation editItemInContainerOrder($orderId: ID!, $skuId: ID!, $input: ContainerOrderItemInput!) {
            editItemInContainerOrder(id: $orderId, skuId: $skuId, input: $input) {
              id
              orderNumber
              groupOrder {
                id
                name
                isOpen
              }
              name
              notes
              createdAt
              totalCost
              account {
                id
                companyName
              }

              placed
              verified
              processed
              leftManufacturer
              shippedFromICP
              delivered

              items {
                id
                skuId
                sku {
                  id
                  options
                  price
                  containerPrice
                  stock
                  product {
                    id
                    name
                    reference
                    attributes
                    images {
                      url
                    }
                    urlSlug
                  }
                }
                qty
                notes
                itemCost
                discountPercent
                totalCost
              }
            }
          }`,
          variables: {
            orderId: this.id,
            skuId,
            input: changes
          }
        })
      } catch (error) {
        adminNotifyError(this.$q, 'Failed to update item in order.', error)
      }
    },
    async editItemQty (row) {
      const result = await this.asyncDialog({
        html: true,
        style: 'width: 50%',
        title: `Update ${row.sku?.options?.colour?.group} (${row.sku?.options?.colour?.name}) ${row.sku?.product?.name} Qty`,
        message: `<p>You're about to update the quantity for <b>${row.sku?.options?.colour?.group} (${row.sku?.options?.colour?.name}) ${row.sku?.product?.name}</b>.</p>
<p style="text-align: center"><img src="${row.sku?.product?.images?.[0]?.url}" style="width: 100px; height: 100px; text-align: center" /></p>
<p>You currently have <b>${row.qty}</b> in your order.</p>
Enter the new quantity for this order:`,
        persistent: true,
        cancel: true,
        prompt: {
          type: 'number',
          model: row.qty
        }
      })

      let newQty = Number(result)
      if (newQty === row.qty) return

      if (newQty < 0) {
        await this.asyncDialog({
          html: true,
          style: 'width: 50%',
          title: `That don't make sense!`,
          message: `I may be a computer but even I get that you can't have negative qty on an invoice.  Enter a value that's more than zero.`,
          persistent: true,
          ok: `I'll try harder next time`
        })
        return
      }

      try {
        if (newQty === 0) {
          await this.$apollo.mutate({
            mutation: gql`mutation deleteItemFromContainerOrder($orderId: ID!, $skuId: ID!) {
              deleteItemFromContainerOrder(id: $orderId, skuId: $skuId) {
                id
                orderNumber
                name
                notes
                createdAt
                totalCost
                account {
                  id
                  companyName
                }
                placed
                verified
                processed
                leftManufacturer
                shippedFromICP
                delivered
                items {
                  id
                  skuId
                  sku {
                      id
                      options
                      price
                      containerPrice
                      stock
                      product {
                          id
                          name
                          reference
                          attributes
                          images {
                            url
                          }
                          urlSlug
                      }
                  }
                  qty
                  notes
                  itemCost
                  discountPercent
                  totalCost
                }
              }
            }`,
            variables: {
              orderId: this.id,
              skuId: row.sku.id
            }
          })
        } else if (newQty > 0) {
          await this.$apollo.mutate({
            mutation: gql`mutation editItemInContainerOrder($orderId: ID!, $skuId: ID!, $input: ContainerOrderItemInput!) {
              editItemInContainerOrder(id: $orderId, skuId: $skuId, input: $input) {
                id
                orderNumber
                name
                notes
                createdAt
                totalCost
                account {
                  id
                  companyName
                }
                placed
                verified
                processed
                leftManufacturer
                shippedFromICP
                delivered
                items {
                  id
                  skuId
                  sku {
                      id
                      options
                      price
                      containerPrice
                      stock
                      product {
                          id
                          name
                          reference
                          attributes
                          images {
                            url
                          }
                          urlSlug
                      }
                  }
                  qty
                  notes
                  itemCost
                  discountPercent
                  totalCost
                }
              }
            }`,
            variables: {
              orderId: this.id,
              skuId: row.sku.id,
              input: {
                qty: newQty
              }
            }
          })
        }
      } catch (error) {
        adminNotifyError(this.$q, 'Error updating!', error)
      }
    },
    async updateVerified (newVal, update) {
      if (newVal !== null) {
        await this.asyncDialog({
          title: `Verify Order ${this.containerOrder.orderNumber}`,
          message: `Are you sure you want to verify this order?  This will:<br/>
- Send the user an email<br/>
- Create an invoice in Xero`,
          html: true,
          cancel: true
        })
      } else {
        await this.asyncDialog({
          title: `Un-Verify Order ${this.containerOrder.orderNumber}`,
          message: `Are you sure you want to unverify this order? The previous Xero order will still exist.  You'll have to clean that up manually.`,
          html: true,
          cancel: true
        })
      }
      update(newVal)
    },
    async deleteItem (row) {
      await this.asyncDialog({
        html: true,
        style: 'width: 50%',
        title: `Update Stock for ${row.sku?.options?.colour?.group} (${row.sku?.options?.colour?.name}) ${row.sku?.product?.name}`,
        message: `<p>You're about to remove <b>${row.sku?.product?.name} in ${row.sku?.options?.colour?.name}</b> from this container order.</p>`,
        persistent: true,
        cancel: true,
        ok: 'Make the change'
      })
      try {
        await this.$apollo.mutate({
          mutation: gql`mutation deleteItemFromContainerOrder($orderId: ID!, $skuId: ID!) {
            deleteItemFromContainerOrder(id: $orderId, skuId: $skuId) {
              id
              orderNumber
              name
              notes
              createdAt
              totalCost
              account {
                id
                companyName
              }
              placed
              verified
              processed
              leftManufacturer
              shippedFromICP
              delivered
              items {
                id
                skuId
                sku {
                    id
                    options
                    price
                    containerPrice
                    stock
                    product {
                        id
                        name
                        reference
                        attributes
                        images {
                          url
                        }
                        urlSlug
                    }
                }
                qty
                notes
                itemCost
                discountPercent
                totalCost
              }
            }
          }`,
          variables: {
            orderId: this.id,
            skuId: row.sku.id
          }
        })
      } catch (error) {
        adminNotifyError(this.$q, 'Error updating!', error)
      }
    },
    async addItem (item) {
      try {
        await this.$apollo.mutate({
          mutation: gql`mutation addItemToContainerOrder($orderId: ID!, $input: ContainerOrderItemInput!) {
            addItemToContainerOrder(id: $orderId, input: $input) {
              id
              orderNumber
              name
              notes
              createdAt
              totalCost
              account {
                id
                companyName
              }
              placed
              verified
              processed
              leftManufacturer
              shippedFromICP
              delivered
              items {
                id
                skuId
                sku {
                    id
                    options
                    price
                    containerPrice
                    stock
                    product {
                        id
                        name
                        reference
                        attributes
                        images {
                          url
                        }
                        urlSlug
                    }
                }
                qty
                notes
                itemCost
                discountPercent
                totalCost
              }
            }
          }`,
          variables: {
            orderId: this.id,
            input: {
              skuId: item.skuId,
              qty: 1
            }
          }
        })
      } catch (error) {
        adminNotifyError(this.$q, 'Error updating!', error)
      }
      this.productToAdd = null
    },
    async filterFn (val, update) {
      this.searchTerm = val
      this.prodUpdateFunc = update
    },
    discountWholeOrder () {
      this.$q.dialog({
        title: 'Apply Discount',
        message: `What % discount do you want to apply to all items in the order?`,
        on: 'Apply',
        cancel: true,
        prompt: {
          model: '',
          type: 'number'
        }
      }).onOk(async (discountPercent) => {
        try {
          await this.$apollo.mutate({
            mutation: gql`mutation setDiscountOnAllContainerOrderItems($orderId: ID!, $discountPercent: Float!) {
            setDiscountOnAllContainerOrderItems(id: $orderId, discountPercent: $discountPercent) {
              id
              orderNumber
              groupOrder {
                id
                name
                isOpen
              }
              name
              notes
              createdAt
              totalCost
              account {
                id
                companyName
              }

              placed
              verified
              processed
              leftManufacturer
              shippedFromICP
              delivered

              items {
                id
                skuId
                sku {
                  id
                  options
                  price
                  containerPrice
                  stock
                  product {
                    id
                    name
                    reference
                    attributes
                    images {
                      url
                    }
                    urlSlug
                  }
                }
                qty
                notes
                itemCost
                discountPercent
                totalCost
              }
            }
          }`,
            variables: {
              orderId: this.id,
              discountPercent: Number(discountPercent)
            }
          })
        } catch (error) {
          adminNotifyError(this.$q, 'Failed to set discount.', error)
        }
      })
    }
  },
  data () {
    return {
      order: {},
      searchTerm: '',
      addProductsMode: true,
      prodUpdateFunc: null,
      orderColumns: [
        { name: 'img', field: row => row.sku?.product?.images?.[0]?.url },
        { name: 'reference', label: 'Reference', field: row => row.sku?.product?.reference, sortable: true, align: 'left' },
        { name: 'brand', label: 'Brand', field: row => row.sku?.product?.attributes?.brand, sortable: true, align: 'left' },
        { name: 'range', label: 'Range', field: row => row.sku?.product?.attributes?.range, sortable: true, align: 'left' },
        { name: 'name', label: 'Name', field: row => row.sku?.product?.name || 'Not Found', sortable: true, align: 'left' },
        // { name: 'size', label: 'Size', field: row => row.sku?.product?.attributes?.size, sortable: true },
        // { name: 'grip', label: 'Grip', field: row => row.sku?.product?.attributes?.grip, sortable: true },
        { name: 'colour', label: 'Colour', field: row => colourToString(row.sku?.options?.colour), sortable: true, align: 'left' },
        { name: 'holds', label: 'Holds', field: row => Number(row.sku?.product?.attributes?.holds), sortable: true, align: 'right' },
        { name: 'price', label: 'Price', field: row => Number(row.itemCost), sortable: true, format: formatCurrency },
        { name: 'qty', label: 'Qty', field: row => Number(row.qty), sortable: true },
        { name: 'discountPercent', label: 'Discount', field: row => Number(row.discountPercent), sortable: true, format: v => `${Number(v).toFixed(1)}%` },
        { name: 'total', label: 'Total', field: row => Number(row.totalCost), sortable: true, format: formatCurrency },
        { name: 'actions', align: 'center', required: true, label: 'Actions', field: '', sortable: false }
      ],
      summaryColumns: [
        { name: 'brand', required: true, label: 'Brand', align: 'left', field: row => row.group, sortable: true },
        { name: 'holds', required: true, label: 'Holds', align: 'left', field: row => row.value, sortable: true }
      ],
      productToAdd: null,
      filteredProducts: []
    }
  }
}
</script>
